import { type addNewBackground, updateImageSuccess } from 'reactApp/modules/aiTools/aiTools.module';
import { aiBckgrRmvDebugLogger, getFileAsDataUrl, processedFileCache } from 'reactApp/modules/aiTools/helpers/ImageTools.helpers';
import { sendErrorRadar } from 'reactApp/modules/aiTools/helpers/SendErrorRadar';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SNACKBAR_CLOSE_TIMEOUT_LOW } from 'reactApp/modules/snackbar/snackbar.constants';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { dataURItoByteArray } from 'reactApp/utils/base64';
import { sendXray } from 'reactApp/utils/ga';
import { put, select } from 'redux-saga/effects';

const imageLoad = async (src: string) =>
    new Promise((resolve) => {
        let image: HTMLImageElement | null = new Image();
        image.src = src;
        image.onload = () =>
            resolve({
                image,
                dispose: () => {
                    image = null;
                },
            });
        image.onerror = () => resolve(null);
    });

function* processError(text = '') {
    aiBckgrRmvDebugLogger.error('handleAddNewBackground - Не удалось скачать картинку для фона!');
    sendXray(['ai-bkgrnd-rmv-img-error-backimg', text]);
    sendErrorRadar('rmbkaddnw', text);

    yield put(
        showSnackbarAction({
            id: 'imageRmvBgrError',
            type: SnackbarTypes.failure,
            text: 'Не удалось добавить новый фон',
            closable: true,
            autoWidth: true,
            closeTimeout: SNACKBAR_CLOSE_TIMEOUT_LOW,
        })
    );
}

function calculateCoverDimensions(containerWidth, containerHeight, imageWidth, imageHeight) {
    const containerRatio = containerWidth / containerHeight;
    const imageRatio = imageWidth / (imageHeight || 1) || 1;

    let newWidth;
    let newHeight;

    if (containerRatio > imageRatio) {
        // Container is wider relative to its height than the image.
        // The image must be scaled to match the container's width,
        // and its height will exceed the container's height.
        newWidth = containerWidth;
        newHeight = containerWidth / imageRatio;
    } else {
        // Container is taller relative to its width than the image.
        // The image must be scaled to match the container's height,
        // and its width will exceed the container's width.
        newHeight = containerHeight;
        newWidth = containerHeight * imageRatio;
    }

    // Calculate the offset to center the image within the container
    const marginLeft = (newWidth - containerWidth) / 2;
    const marginTop = (newHeight - containerHeight) / 2;

    return {
        width: newWidth,
        height: newHeight,
        marginLeft: -marginLeft, // Use negative margin to center the excess
        marginTop: -marginTop,
    };
}

export function* handleAddNewBackground({ payload }: ReturnType<typeof addNewBackground>) {
    try {
        const selectedId = (yield select(SelectionsSelectors.getSelectedIdxs)).pop();
        const item = yield select(getStorageItemById, EStorageType.gallery, selectedId);

        const { id, type, pathId } = payload;

        const transparent = processedFileCache.getTansparent(pathId);

        const blob = new Blob([transparent], { type: 'image/png' });
        const imageResultTransparent = URL.createObjectURL(blob);

        if (payload.type === 'transparent') {
            processedFileCache.setNewBackground(pathId);
            yield put(updateImageSuccess({ id: pathId, imageResult: imageResultTransparent }));
            return;
        }

        const image = yield imageLoad(imageResultTransparent);

        if (!image?.image) {
            yield processError('img-load-fail');
            return;
        }

        let canvas: HTMLCanvasElement | null = document.createElement('canvas');
        let ctx = canvas.getContext('2d');

        if (!ctx) {
            aiBckgrRmvDebugLogger.error('handleAddNewBackground - Не удалось создать контекст!');
            sendXray('ai-bkgrnd-rmv-img-error-ctx');
            yield processError('noctx');
            return;
        }

        canvas.width = image.image.width;
        canvas.height = image.image.height;

        if (type === 'color') {
            ctx.fillStyle = id;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        } else if (type === 'mail' || type === 'cloud') {
            let url = id;
            let ext = id.split('.').pop();
            if (type === 'cloud') {
                url = item?.thumbnails?.original;
                ext = item?.ext;
            }
            const imageBackDataUrl = yield getFileAsDataUrl(url, ext, true, type === 'cloud');
            const imageBack = yield imageLoad(imageBackDataUrl);

            if (!imageBack?.image) {
                yield processError('img-load-fail2');
                return;
            }

            const { width, height, marginTop, marginLeft } = calculateCoverDimensions(
                canvas.width,
                canvas.height,
                imageBack.image.width,
                imageBack.image.height
            );

            ctx.drawImage(imageBack.image, marginLeft, marginTop, width, height);
            imageBack.dispose();
            URL.revokeObjectURL(imageBackDataUrl);
        }

        ctx.drawImage(image.image, 0, 0);
        image.dispose();
        URL.revokeObjectURL(imageResultTransparent);

        const imageResult = canvas.toDataURL('image/jpeg', 0.9);

        if (!imageResult) {
            aiBckgrRmvDebugLogger.error('handleAddNewBackground - Не удалось взять картинку с канваса!');
            sendXray('ai-bkgrnd-rmv-img-error-canvas');
            yield processError('img-cnvs-fail');
            return;
        }
        yield put(updateImageSuccess({ id: pathId, imageResult, isNewbackground: type !== 'transparent' }));

        yield put(
            showSnackbarAction({
                id: 'imageRmvBgrSuccess',
                type: SnackbarTypes.success,
                text: 'Фон добавлен',
                closable: true,
                autoWidth: true,
                closeTimeout: SNACKBAR_CLOSE_TIMEOUT_LOW,
            })
        );

        processedFileCache.setNewBackground(pathId, dataURItoByteArray(imageResult)?.buffer);

        ctx = null;
        canvas = null;
    } catch (error: any) {
        yield processError(error?.message);
    }
}
